import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaHeart, FaMapMarkerAlt, FaUsers, FaCalendar, FaMountain, FaTree, FaWater, FaSun, FaLandmark, FaFire, FaArrowDown, FaTint, FaSnowflake, FaLeaf, FaGem, FaMonument } from 'react-icons/fa';

// Priority parks to show first (customize this list)
const PRIORITY_PARK_CODES = [
  'YOSE', 'ZION', 'GRCA', 'YELL', 'GLAC', 'ROMO',
  'OLYM', 'ACAD', 'GRSM', 'ARCH', 'BRCA', 'JOTR',
  'REDW', 'SEQU', 'DEVA', 'CHIS', 'CRLA', 'GRTE'
];

const NationalParksGrid = ({ onParkSelect }) => {
  const [favorites, setFavorites] = useState(new Set());
  const [activeFilter, setActiveFilter] = useState('all');
  const [displayedParks, setDisplayedParks] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState([{ id: 'all', label: 'All Parks', icon: null }]);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const observerTarget = useRef(null);

  const ITEMS_PER_PAGE = 12;
  const SUPABASE_API_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || "YOUR_SUPABASE_ANON_KEY_HERE";
  const SUPABASE_URL = 'https://bylolucjhzsbbouoalah.supabase.co';

  // Predefined filter categories with icons
  const FILTER_CATEGORIES = [
    { id: 'all', label: 'All Parks', icon: null },
    { id: 'mountains', label: 'Mountains', icon: FaMountain },
    { id: 'desert', label: 'Desert', icon: FaSun },
    { id: 'forest', label: 'Forest', icon: FaTree },
    { id: 'wetlands', label: 'Wetlands', icon: FaWater },
    { id: 'coastal', label: 'Coastal', icon: FaWater },
    { id: 'islands', label: 'Islands', icon: FaLandmark },
    { id: 'volcanic', label: 'Volcanic', icon: FaFire },
    { id: 'canyon', label: 'Canyon', icon: FaArrowDown },
    { id: 'river', label: 'River', icon: FaTint },
    { id: 'lake', label: 'Lake', icon: FaTint },
    { id: 'glacier', label: 'Glacier', icon: FaSnowflake },
    { id: 'grassland', label: 'Grassland', icon: FaLeaf },
    { id: 'cave', label: 'Cave', icon: FaGem },
    { id: 'historic', label: 'Historic', icon: FaMonument },
    { id: 'monument', label: 'Monument', icon: FaMonument },
  ];

  // Memoize the sort function
  const sortByPriority = useCallback((parks) => {
    const sorted = [...parks].sort((a, b) => {
      const priorityA = PRIORITY_PARK_CODES.indexOf(a.code);
      const priorityB = PRIORITY_PARK_CODES.indexOf(b.code);
      
      const aIsPriority = priorityA !== -1;
      const bIsPriority = priorityB !== -1;
      
      // Priority parks first, then by their position in priority list
      if (aIsPriority && !bIsPriority) return -1;
      if (!aIsPriority && bIsPriority) return 1;
      if (aIsPriority && bIsPriority) return priorityA - priorityB;
      
      // Non-priority parks by code alphabetically
      return a.code.localeCompare(b.code);
    });
    
    return sorted;
  }, []);

  // Load available features on mount
  useEffect(() => {
    setFilters(FILTER_CATEGORIES);
    setFiltersLoading(false);
  }, []);

  // Fetch parks with infinite scroll and feature filtering
  const fetchMoreParks = useCallback(async () => {
    if (loading || !hasMore) return;
    
    setLoading(true);

    try {
      let parks = [];
      let rawParksCount = 0; // Track count before filtering
      
      // If no filter is active, prioritize showing priority parks first
      // If filter is active, use normal pagination to show all matching parks
      if (page === 0 && activeFilter === 'all') {
        // On first page with no filter, fetch priority parks first
        const priorityQuery = PRIORITY_PARK_CODES.map(code => `code.eq.${code}`).join(',');
        const query = `${SUPABASE_URL}/rest/v1/national_parks?select=*&or=(${priorityQuery})`;
        
        const res = await fetch(query, {
          headers: {
            apikey: SUPABASE_API_KEY,
            Authorization: `Bearer ${SUPABASE_API_KEY}`,
          },
        });
        
        if (res.ok) {
          parks = await res.json();
          rawParksCount = parks.length;
        }
      } else {
        // For filtered views or non-first pages, use standard pagination
        const fetchLimit = activeFilter !== 'all' ? ITEMS_PER_PAGE * 4 : ITEMS_PER_PAGE;
        const offset = page * fetchLimit;
        const query = `${SUPABASE_URL}/rest/v1/national_parks?select=*&limit=${fetchLimit}&offset=${offset}`;
        
        const res = await fetch(query, {
          headers: {
            apikey: SUPABASE_API_KEY,
            Authorization: `Bearer ${SUPABASE_API_KEY}`,
          },
        });
        
        if (res.ok) {
          parks = await res.json();
          rawParksCount = parks.length;
        }
      }
      
      // Filter by active filter if not "all"
      if (activeFilter !== 'all') {
        parks = parks.filter(park => 
          park.features && Array.isArray(park.features) && park.features.includes(activeFilter)
        );
      }
      
      // Deduplicate and sort by priority
      setDisplayedParks(prevDisplayed => {
        const existingCodes = new Set(prevDisplayed.map(p => p.code));
        const newParks = parks.filter(p => !existingCodes.has(p.code));
        const combined = [...prevDisplayed, ...newParks];
        return sortByPriority(combined);
      });
      
      // Check if we should continue loading based on raw count from DB
      // Continue if we got a full batch, stop only if DB returned fewer parks than requested
      const fetchLimit = activeFilter !== 'all' ? ITEMS_PER_PAGE * 4 : ITEMS_PER_PAGE;
      setHasMore(rawParksCount >= fetchLimit);
      setPage(prev => prev + 1);
    } catch (err) {
      console.error('Error fetching parks:', err);
    } finally {
      setLoading(false);
    }
  }, [page, hasMore, loading, sortByPriority, activeFilter, SUPABASE_API_KEY, SUPABASE_URL]);

  // Reset pagination when filter changes
  useEffect(() => {
    setDisplayedParks([]);
    setPage(0);
    setHasMore(true);
  }, [activeFilter]);

  // Initial load
  useEffect(() => {
    if (displayedParks.length === 0 && page === 0) {
      fetchMoreParks();
    }
  }, [fetchMoreParks]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          fetchMoreParks();
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) observer.observe(observerTarget.current);
    return () => observer.disconnect();
  }, [hasMore, loading, fetchMoreParks]);

  const toggleFavorite = (id) => {
    setFavorites(prev => {
      const newFavs = new Set(prev);
      if (newFavs.has(id)) {
        newFavs.delete(id);
      } else {
        newFavs.add(id);
      }
      return newFavs;
    });
  };

  return (
    <div>
      <div className="mb-8">
        <p className="text-gray-600 dark:text-gray-400 mb-6">Browse and plan your next adventure</p>
        
        <div className="flex gap-3 flex-wrap">
          {filters.map((filter) => {
            const Icon = filter.icon;
            return (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`px-4 py-2 rounded-full shadow-sm border transition-all flex items-center gap-2 ${
                  activeFilter === filter.id
                    ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                }`}
              >
                {Icon && <Icon className="w-4 h-4" />}
                {filter.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
          style={{
            transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          {displayedParks.map((park) => (
            <div 
              key={park.code}
              className="group animate-in fade-in zoom-in duration-300"
              style={{
                animation: 'fadeIn 0.5s ease-out'
              }}
            >
              <div 
                className="h-72 rounded-2xl overflow-hidden relative cursor-pointer transition-all hover:scale-[1.03] hover:shadow-2xl shadow-md bg-gradient-to-br from-gray-400 to-gray-600"
                onClick={() => onParkSelect && onParkSelect(park.code)}
              >
                {park.image_url && (
                  <img 
                    src={park.image_url} 
                    alt={park.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-80 group-hover:opacity-90 transition" />
                
                <button
                  onClick={() => toggleFavorite(park.code)}
                  className="absolute top-3 right-3 p-2 bg-white/90 backdrop-blur rounded-full hover:bg-white transition z-10"
                >
                  <FaHeart 
                    className={`w-5 h-5 transition ${favorites.has(park.code) ? 'fill-red-500 text-red-500' : 'text-gray-700'}`}
                  />
                </button>
                
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                  <h3 className="text-2xl font-bold mb-1">{park.name}</h3>
                  <div className="flex items-center gap-1 text-sm text-white/90 mb-3">
                    <FaMapMarkerAlt className="w-4 h-4" />
                    <span>{park.state}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {displayedParks.length === 0 && (
          <div className="text-center py-20 text-gray-500">
            <p className="text-lg">No parks found for this filter</p>
          </div>
        )}

        {/* Infinite scroll trigger */}
        <div ref={observerTarget} className="py-8 text-center">
          {loading && <p className="text-gray-500">Loading more parks...</p>}
          {!hasMore && displayedParks.length > 0 && <p className="text-gray-500">No more parks to load</p>}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default NationalParksGrid;
